import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Briefcase,
  Calendar,
  Check,
  CheckCircle2,
  Boxes,
  Cog,
  FlaskConical,
  GraduationCap,
  Hammer,
  Layers,
  Mic,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import Button from '../components/UI/Button';
import HeroBackdrop from '../components/UI/HeroBackdrop';
import { heroCtaBand, heroImageFade, heroShell } from '../content/heroThemes';
import CourseFee from '../components/UI/CourseFee';
import CourseCoverImage from '../components/content/CourseCoverImage';
import { COURSE_IMAGES } from '../config/courseImages';
import { formatEventWhen, speakerHeadline } from '../utils/eventDisplay';
import { BRAND } from '../config/brand';
import { HOME_CONTENT } from '../content/homePage';
import { PROGRAM_SERIES } from '../content/programSeries';
import { contentApi } from '../services/contentApi';
import { useAuth } from '../context/AuthContext';
import { isInterviewer, isMentor } from '../utils/roles';
import { CourseListItem, EventListItem, GigListItem } from '../types/content';
import { isNavMenuVisible } from '../utils/navMenus';
import { useMyContributorApplications } from '../hooks/useMyContributorApplications';
import { usePublicNav } from '../hooks/usePublicNav';
import { contributorStatusLabel } from '../utils/contributorApplications';

const TRAINING_ICONS = [Target, TrendingUp, Briefcase];
const METHOD_ICONS = [BookOpen, Hammer, Layers, Boxes, Cog, Sparkles, ShieldCheck, CheckCircle2];
const AUDIENCE_ICONS = [GraduationCap, UserPlus, Briefcase];
const WHY_ICONS = [Users, Sparkles, CheckCircle2];

const HOME_FEATURED_SLUGS = [
  'ai-native-software-engineering-net-python',
  'generative-ai-with-dotnet',
  'software-architecture-system-design',
];

function pickHomeCourses(list: CourseListItem[]) {
  const preferred = HOME_FEATURED_SLUGS
    .map((slug) => list.find((course) => course.slug === slug))
    .filter((course): course is CourseListItem => !!course);
  const rest = list.filter((course) => !HOME_FEATURED_SLUGS.includes(course.slug));
  return [...preferred, ...rest].slice(0, 3);
}

function JourneyRibbon({ steps, tone }: { steps: string[]; tone: 'primary' | 'secondary' }) {
  const chip = tone === 'primary' ? 'bg-primary-600 text-white' : 'bg-secondary-600 text-white';
  return (
    <div className="mt-5 rounded-2xl bg-white/70 border border-slate-200/70 backdrop-blur-sm p-4 md:p-5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-3">
        {steps.map((step, i) => (
          <React.Fragment key={step}>
            <span className={`inline-flex items-center rounded-full px-3.5 py-2 text-sm font-semibold shadow-sm ${chip}`}>
              {step}
            </span>
            {i < steps.length - 1 ? <ArrowRight className="h-5 w-5 text-gray-400 shrink-0" aria-hidden /> : null}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

const Home: React.FC = () => {
  const { user } = useAuth();
  const { mentor: mentorApplication, interviewer: interviewerApplication, canApplyMentor, canApplyInterviewer } = useMyContributorApplications();
  const programsPath = PROGRAM_SERIES.programsListPath;
  const [courses, setCourses] = useState<CourseListItem[]>([]);
  const [events, setEvents] = useState<EventListItem[]>([]);
  const [gigs, setGigs] = useState<GigListItem[]>([]);
  const { navItems, hasPublishedEvents, hasPublishedGigs } = usePublicNav();

  useEffect(() => {
    contentApi.listCourses({ pageSize: 24, sort: 'featured' }).then((r) => {
      setCourses(pickHomeCourses(r.data.courses || []));
    }).catch(() => undefined);
    contentApi.listEvents({ pageSize: 3 }).then((r) => setEvents(r.data.events || [])).catch(() => undefined);
    contentApi.listGigs({ pageSize: 3 }).then((r) => setGigs(r.data.gigs || [])).catch(() => undefined);
  }, []);

  const navFlags = { hasPublishedEvents, hasPublishedGigs };
  const showCourses = isNavMenuVisible('courses', navItems, navFlags);
  const showPrograms = isNavMenuVisible('programs', navItems, navFlags);
  const showProjects = isNavMenuVisible('projects', navItems, navFlags);
  const showInterviews = isNavMenuVisible('interviews', navItems, navFlags);
  const showGigs = isNavMenuVisible('gigs', navItems, navFlags);
  const showEvents = isNavMenuVisible('events', navItems, navFlags);
  const showTwoPaths = showCourses || showPrograms;
  const buildCards = [showProjects, showInterviews, showGigs].filter(Boolean).length;

  return (
    <div>
      <section className={heroShell('home')}>
        <HeroBackdrop tone="home" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-[1.485rem] md:py-[1.733rem] lg:py-[1.98rem]">
          <div className="grid lg:grid-cols-2 gap-[1.238rem] lg:gap-[1.98rem] items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-200">
                {BRAND.eyebrow}
              </p>
              <h1 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                {HOME_CONTENT.tagline}
              </h1>
              <p className="mt-4 text-sm sm:text-base text-primary-100/90 leading-relaxed">
                {HOME_CONTENT.heroIntro}
              </p>
              {(showPrograms || showCourses) && (
                <div className="mt-4 flex flex-wrap gap-3">
                  {showPrograms && (
                    <Link to={programsPath}>
                      <Button variant="onDark">
                        {HOME_CONTENT.ctas.exploreAcademyPrograms}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                  )}
                  {showCourses && (
                    <Link to="/courses">
                      <Button variant="outline" className="!text-white !border-white/40 hover:!bg-white/10">
                        {HOME_CONTENT.ctas.browseSelfPacedCourses}
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </div>

            <div className="relative">
              <div className="relative overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
                <img
                  src={COURSE_IMAGES.hero}
                  alt="Engineers building AI-native software"
                  className="w-full h-[13.86rem] sm:h-[16.83rem] lg:h-[19.8rem] object-cover object-top"
                />
                <div className={heroImageFade('home')} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {showTwoPaths && (
      <section id="how-you-learn" className="section-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">{HOME_CONTENT.twoPaths.eyebrow}</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">{HOME_CONTENT.twoPaths.title}</h2>
          <p className="mt-4 text-gray-600 max-w-3xl leading-relaxed">
            {HOME_CONTENT.twoPaths.intro}
          </p>

          <div className={`mt-10 grid gap-6 ${showCourses && showPrograms ? 'lg:grid-cols-2' : 'lg:grid-cols-1 max-w-2xl'}`}>
            {showCourses && (
            <div className="surface-card border-2 border-primary-200 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-primary-50 text-primary-700 flex items-center justify-center">
                  <BookOpen className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">Self-paced · Courses</p>
                  <h3 className="text-xl font-bold text-gray-900">{HOME_CONTENT.twoPaths.coursesTitle}</h3>
                </div>
              </div>
              <JourneyRibbon steps={['Course', 'Lab', 'Project', 'Portfolio']} tone="primary" />
              <p className="mt-4 text-gray-600 leading-relaxed">
                {HOME_CONTENT.twoPaths.coursesBody}
              </p>
              <ul className="mt-5 space-y-1.5 text-sm text-gray-700">
                {['Working developers', 'Flexible evenings and weekends', 'Learning a specific stack'].map((item) => (
                  <li key={item} className="flex gap-2"><Check className="h-4 w-4 text-primary-600 mt-0.5 shrink-0" />{item}</li>
                ))}
              </ul>
              <Link to="/courses" className="mt-6 inline-flex">
                <Button>{HOME_CONTENT.ctas.browseSelfPacedCourses}</Button>
              </Link>
            </div>
            )}

            {showPrograms && (
            <div className="surface-card border-2 border-secondary-200 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-secondary-50 text-secondary-700 flex items-center justify-center">
                  <GraduationCap className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-secondary-700">Guided · IDC Academy</p>
                  <h3 className="text-xl font-bold text-gray-900">{HOME_CONTENT.twoPaths.academyTitle}</h3>
                </div>
              </div>
              <JourneyRibbon steps={['Program', 'Mentor', 'Project', 'Review', 'Interview', 'Portfolio']} tone="secondary" />
              <p className="mt-4 text-gray-600 leading-relaxed">
                {HOME_CONTENT.twoPaths.academyBody}
              </p>
              <ul className="mt-5 space-y-1.5 text-sm text-gray-700">
                {HOME_CONTENT.twoPaths.academyPoints.map((item) => (
                  <li key={item} className="flex gap-2"><Check className="h-4 w-4 text-secondary-600 mt-0.5 shrink-0" />{item}</li>
                ))}
              </ul>
              <Link to={programsPath} className="mt-6 inline-flex">
                <Button variant="secondary">{HOME_CONTENT.ctas.exploreAcademyPrograms}</Button>
              </Link>
            </div>
            )}
          </div>
        </div>
      </section>
      )}

      {showCourses && (
      <section id="browse-courses" className="section-band-alt">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Featured Courses</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Courses you can start today</h2>
            <p className="mt-3 text-gray-600 max-w-2xl leading-relaxed">
              Self-paced tracks you can start immediately.
            </p>
          </div>
          <Link to="/courses" className="text-primary-700 font-medium inline-flex items-center">
            View all courses <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </div>
        {courses.length > 0 ? (
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {courses.map((course) => (
              <Link key={course.id} to={`/courses/${course.slug}`} className="surface-card surface-card-hover overflow-hidden">
                <CourseCoverImage src={course.coverImageUrl} />
                <div className="p-6">
                  <p className="text-xs font-semibold uppercase tracking-wide text-primary-600">{course.categoryName || 'Course'}</p>
                  <h3 className="mt-2 text-lg font-bold text-gray-900">{course.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 line-clamp-3">{course.summary || course.subtitle}</p>
                  <p className="mt-3 text-sm font-semibold text-gray-900">
                    <CourseFee fee={Number(course.price || 0)} discount={Number(course.discountPercent || 0)} />
                  </p>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-2xl border border-dashed border-gray-200 bg-gray-50 p-8 text-center">
            <FlaskConical className="h-8 w-8 text-primary-600 mx-auto" />
            <p className="mt-3 text-gray-700">Courses appear here as they are published.</p>
            <Link to="/courses" className="mt-4 inline-flex"><Button>Open the catalog</Button></Link>
          </div>
        )}
      </div>
      </section>
      )}

      <section id="why-idcoder" className="section-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">
            {HOME_CONTENT.training.pointsEyebrow}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
            Sharper skills. Stronger capabilities. A career that compounds.
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4 md:gap-5">
            {HOME_CONTENT.training.points.map((point, index) => {
              const Icon = TRAINING_ICONS[index];
              return (
                <div key={point.title} className="surface-card p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-gray-900">{point.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{point.description}</p>
                  {'roles' in point && point.roles && (
                    <p className="mt-4 text-xs font-semibold tracking-wide text-primary-800">
                      {point.roles.join(' · ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="section-band-alt">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">
            {HOME_CONTENT.method.eyebrow}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
            {HOME_CONTENT.method.title}
          </h2>
          <p className="mt-2 text-sm font-semibold text-primary-800">{HOME_CONTENT.method.promise}</p>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {HOME_CONTENT.method.steps.map((step, index) => {
              const Icon = METHOD_ICONS[index % METHOD_ICONS.length];
              return (
                <div key={step.title} className="relative surface-card p-5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                    <Icon className="h-4 w-4" />
                  </div>
                  <h3 className="mt-3 font-bold text-gray-900">{step.title}</h3>
                  {step.description ? (
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {buildCards > 0 && (
      <section id="build-and-earn" className="section-band">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Build, then put it to work</p>
        <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Projects for evidence. Opportunities for what comes next.</h2>
        <p className="mt-4 text-gray-600 max-w-3xl leading-relaxed">
          {showGigs
            ? 'Self-paced projects you can start today and use in your portfolio. When you\'re ready, book a technical interview for expert feedback, or apply to a gig to earn.'
            : 'Self-paced projects you can start today and use in your portfolio. When you\'re ready, book a technical interview for expert feedback.'}
        </p>
        <div className={`mt-10 grid gap-5 ${buildCards >= 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : buildCards === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-1 max-w-md'}`}>
          {showProjects && (
          <Link to="/projects" className="surface-card surface-card-hover p-5">
            <Briefcase className="h-6 w-6 text-primary-600" />
            <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-primary-600">Self-paced</p>
            <h3 className="mt-1 font-semibold text-gray-900">Projects</h3>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">Self-paced learning briefs with requirements, design, and architecture. Submit your Git repo when you are done.</p>
          </Link>
          )}
          {showInterviews && (
          <Link to="/interviews" className="surface-card surface-card-hover p-5">
            <Users className="h-6 w-6 text-primary-600" />
            <h3 className="mt-3 font-semibold text-gray-900">Interviews</h3>
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">A timed session with an industry interviewer and written feedback.</p>
          </Link>
          )}
          {showGigs && (
            <Link to="/gigs" className="surface-card surface-card-hover p-5">
              <Wallet className="h-6 w-6 text-primary-600" />
              <h3 className="mt-3 font-semibold text-gray-900">Gigs</h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">Paid assignments from clients. Separate from learning projects.</p>
            </Link>
          )}
        </div>
        {showGigs && gigs.length > 0 && (
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            {gigs.map((gig) => (
              <Link key={gig.id} to={`/gigs/${gig.slug}`} className="surface-card surface-card-hover p-5">
                <p className="text-xs uppercase tracking-wide text-primary-600">{gig.clientName}</p>
                <h3 className="mt-2 text-lg font-semibold text-gray-900">{gig.title}</h3>
                <p className="mt-2 text-sm text-gray-600 line-clamp-3">{gig.summary || gig.subtitle}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
      </section>
      )}

      {showInterviews && (
      <section id="interview" className="section-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">
                {HOME_CONTENT.interview.eyebrow}
              </p>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
                {HOME_CONTENT.interview.title}
              </h2>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                {HOME_CONTENT.interview.intro}
              </p>
              <p className="mt-4 text-sm font-semibold text-gray-900">
                {HOME_CONTENT.interview.facts}
              </p>
              <div className="mt-6">
                <Link to={PROGRAM_SERIES.interviewsPath}>
                  <Button variant="primary" size="lg">
                    {HOME_CONTENT.ctas.bookInterview}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-xl shadow-lg ring-1 ring-black/5">
              <img
                src={COURSE_IMAGES.interviewHero}
                alt="Technical interview with an industry expert"
                className="w-full h-[14rem] sm:h-[16rem] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
      )}

      <section id="who-its-for" className="section-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">
            {HOME_CONTENT.audience.eyebrow}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
            {HOME_CONTENT.audience.title}
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4 md:gap-5">
            {HOME_CONTENT.audience.items.map((item, index) => {
              const Icon = AUDIENCE_ICONS[index];
              return (
                <div key={item.title} className="surface-card p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-gray-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="why-learn" className="section-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-14">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary-600">
            {HOME_CONTENT.why.eyebrow}
          </p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
            {HOME_CONTENT.why.title}
          </h2>
          <div className="mt-8 grid md:grid-cols-3 gap-4 md:gap-5">
            {HOME_CONTENT.why.items.map((item, index) => {
              const Icon = WHY_ICONS[index];
              return (
                <div key={item.title} className="surface-card p-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-bold text-gray-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="contribute" className="section-band">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Get involved</p>
          <h2 className="mt-2 text-2xl md:text-3xl font-bold text-gray-900">
            {HOME_CONTENT.contribute.title}
          </h2>
          <p className="mt-3 text-lg font-semibold text-gray-900">{HOME_CONTENT.contribute.subtitle}</p>
          <p className="mt-3 text-gray-600 max-w-3xl leading-relaxed">
            {HOME_CONTENT.contribute.intro}
          </p>
          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            <div className="surface-card border-2 border-primary-200 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-primary-50 text-primary-700 flex items-center justify-center">
                  <GraduationCap className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{HOME_CONTENT.contribute.mentor.title}</h3>
              </div>
              <p className="mt-4 text-gray-600 leading-relaxed">{HOME_CONTENT.contribute.mentor.description}</p>
              {canApplyMentor ? (
                <Link to={HOME_CONTENT.contribute.mentor.path} className="mt-6 inline-flex">
                  <Button>
                    {HOME_CONTENT.contribute.mentor.cta}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              ) : (
                <p className="mt-6 text-sm font-semibold text-gray-800">
                  {isMentor(user)
                    ? 'You are already a mentor.'
                    : `Mentor application: ${contributorStatusLabel(mentorApplication)}`}
                </p>
              )}
            </div>
            <div className="surface-card border-2 border-secondary-200 p-6 md:p-8">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-secondary-50 text-secondary-700 flex items-center justify-center">
                  <Mic className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">{HOME_CONTENT.contribute.interviewer.title}</h3>
              </div>
              <p className="mt-4 text-gray-600 leading-relaxed">{HOME_CONTENT.contribute.interviewer.description}</p>
              {canApplyInterviewer ? (
                <Link to={HOME_CONTENT.contribute.interviewer.path} className="mt-6 inline-flex">
                  <Button variant="secondary">
                    {HOME_CONTENT.contribute.interviewer.cta}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              ) : (
                <p className="mt-6 text-sm font-semibold text-gray-800">
                  {isInterviewer(user)
                    ? 'You are already an interviewer.'
                    : `Interviewer application: ${contributorStatusLabel(interviewerApplication)}`}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {showEvents && (
        <section id="explore-events" className="section-band py-14 md:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">Explore events</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">Learn with the community</h2>
            <p className="mt-4 text-gray-600 max-w-3xl leading-relaxed">
              Workshops, technical sessions, and developer meetups. Enroll from the event page — online links are shared after approval.
            </p>
            {events.length > 0 && (
              <div className="mt-10 grid md:grid-cols-3 gap-5">
                {events.map((event) => (
                  <Link key={event.id} to={`/events/${event.slug}`} className="surface-card surface-card-hover overflow-hidden">
                    <CourseCoverImage
                      src={event.coverImageUrl}
                      fallbackSrc={COURSE_IMAGES.eventsHero}
                      className="h-40 w-full object-cover bg-gray-100"
                    />
                    <div className="p-5">
                      <p className="text-xs uppercase tracking-wide text-primary-600 inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" /> {formatEventWhen(event.startsAt, event.endsAt)}
                      </p>
                      <h3 className="mt-2 font-semibold text-gray-900">{event.title}</h3>
                      {speakerHeadline(event) && (
                        <p className="mt-2 text-sm font-medium text-gray-800">{speakerHeadline(event)}</p>
                      )}
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">{event.summary || event.subtitle}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
            <Link to="/events" className="mt-8 inline-flex"><Button>Explore events</Button></Link>
          </div>
        </section>
      )}

      {(showPrograms || showCourses || showInterviews) && (
      <section id="next-step" className={heroCtaBand('home')}>
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold">{HOME_CONTENT.finalCta.title}</h2>
          <p className="mt-3 text-primary-100 max-w-2xl mx-auto leading-relaxed">
            {HOME_CONTENT.finalCta.intro}
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            {showPrograms && (
            <Link to={programsPath}>
              <Button variant="onDark" size="lg">
                {HOME_CONTENT.ctas.exploreAcademyPrograms}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            )}
            {showCourses && (
            <Link to="/courses">
              <Button variant="outline" size="lg" className="border-white/40 text-white hover:bg-white/10">
                {HOME_CONTENT.ctas.browseSelfPacedCourses}
              </Button>
            </Link>
            )}
            {showInterviews && (
            <Link to={PROGRAM_SERIES.interviewsPath}>
              <Button variant="outline" size="lg" className="border-white/40 text-white hover:bg-white/10">
                {HOME_CONTENT.ctas.bookInterview}
              </Button>
            </Link>
            )}
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

export default Home;
